(() => {
  if (window.LEDTextPreviewRenderer) return;

  // Character size is fixed by the selected size tier. Short text is never
  // enlarged to fill the available path; only overlong text may scale down.
  const textOccupancyRatio = 0.90;
  const buildSizeRule = (originalLongestSideCm, characterCm, rateCents, maxCharacters) => ({
    maxCharacters,
    rateCents,
    characterWidthCm: characterCm,
    characterHeightCm: characterCm,
    originalLongestSideCm,
    maxTextWidthCm: originalLongestSideCm * textOccupancyRatio,
    textOccupancyRatio
  });

  const sizeRules = {
    20: buildSizeRule(50, 6, 1100, 7),
    30: buildSizeRule(75, 6, 1100, 11),
    35: buildSizeRule(88, 6, 1100, 13),
    40: buildSizeRule(100, 10, 1500, 11),
    45: buildSizeRule(114, 10, 1500, 13),
    50: buildSizeRule(125, 10, 1500, 14),
    60: buildSizeRule(150, 10, 1500, 16)
  };

  const productColors = {
    white: '#FFFFFF',
    pink: '#FF28D7',
    red: '#FF1F1F',
    orange: '#FF9E24',
    yellow: '#FFF21A',
    blue: '#1827E8',
    azure: '#24E4E8',
    green: '#25ED3C',
    purple: '#8F2BD1'
  };

  const previewFontFamily = 'Arial, sans-serif';
  const contourCutClearanceCm = 1;
  const tubeDiameterCm = 0.5;
  const artworkTextGapCm = 2;

  class LEDTextPreviewRenderer {
    constructor() {
      this.nativeTextMeasureCache = new Map();
      this.glyphGeometryCache = new Map();
      this.singleLineGlyphData = window.BottomTextSingleLineFont || {};
      this.artworkJoinProfile = [[0, 1], [1, 1]];
    }

    getPreviewGeometry(width, height, state, ratios) {
      const artwork = {
        centerX: this.readRatio(ratios[0], 0.5) * width,
        centerY: this.readRatio(ratios[1], 0.5) * height,
        width: this.readRatio(ratios[2], 0.7, 0.05) * width,
        height: this.readRatio(ratios[3], 0.7, 0.05) * height
      };
      const referenceSpan = state.layout === 'vertical' ? artwork.height : artwork.width;
      const pixelsPerCm = referenceSpan / state.rules.originalLongestSideCm;
      const characterAdvance = pixelsPerCm * state.textLayout.renderedCellCm;
      return {
        artwork,
        characterAdvance,
        fontSize: characterAdvance * 0.94,
        gap: pixelsPerCm * artworkTextGapCm,
        height,
        pixelsPerCm,
        tubeDiameter: pixelsPerCm * tubeDiameterCm,
        width
      };
    }

    readRatio(value, fallback, minimum = 0) {
      const number = Number(value);
      if (!Number.isFinite(number)) return fallback;
      return Math.min(1, Math.max(minimum, number));
    }

    renderLayoutPreview(preview, state, geometry) {
      const canvas = preview.querySelector('[data-bottom-text-render-canvas]');
      if (!(canvas instanceof HTMLCanvasElement)) return;

      const ratio = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
      const width = Math.max(1, Math.ceil(geometry.width));
      const height = Math.max(1, Math.ceil(geometry.height));
      canvas.width = Math.ceil(width * ratio);
      canvas.height = Math.ceil(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const context = canvas.getContext('2d');
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      if (state.layout === 'round') {
        this.renderRoundLayout(context, state, geometry);
      } else if (state.layout === 'vertical') {
        this.renderVerticalLayout(context, state, geometry);
      } else {
        this.renderHorizontalLayout(context, state, geometry);
      }
    }

    renderHorizontalLayout(context, state, geometry) {
      const characters = Array.from(state.text);
      const span = geometry.characterAdvance * characters.length;
      const startX = geometry.artwork.centerX - span / 2;
      const artworkBottom = geometry.artwork.centerY + geometry.artwork.height / 2;
      const baseline = artworkBottom + geometry.gap + geometry.fontSize * 0.78;
      const tubeDiameter = geometry.tubeDiameter;
      const backboardPadding = geometry.pixelsPerCm * contourCutClearanceCm;

      this.drawHorizontalContourBackboard(context, state, {
        ...geometry,
        artworkBottom,
        backboardPadding,
        baseline,
        startX,
        tubeDiameter
      });

      characters.forEach((character, index) => {
        const x = startX + geometry.characterAdvance * (index + 0.5);
        this.drawGlowCharacter(context, character, x, baseline, 0, geometry.fontSize, state.color.value, 'alphabetic', tubeDiameter);
      });
    }

    renderVerticalLayout(context, state, geometry) {
      const characters = Array.from(state.text);
      const span = geometry.characterAdvance * characters.length;
      const artworkRight = geometry.artwork.centerX + geometry.artwork.width / 2;
      const startY = geometry.artwork.centerY - span / 2;
      const textX = artworkRight + geometry.gap + geometry.fontSize * 0.58;
      const tubeDiameter = geometry.tubeDiameter;
      const backboardPadding = geometry.pixelsPerCm * contourCutClearanceCm;

      this.drawVerticalContourBackboard(context, state, {
        ...geometry,
        artworkRight,
        backboardPadding,
        span,
        startY,
        textX,
        tubeDiameter
      });

      characters.forEach((character, index) => {
        const y = startY + geometry.characterAdvance * (index + 0.5);
        this.drawGlowCharacter(context, character, textX, y, 0, geometry.fontSize, state.color.value, 'middle', tubeDiameter);
      });
    }

    renderRoundLayout(context, state, geometry) {
      const characters = Array.from(state.text);
      const radius = Math.min(geometry.artwork.width, geometry.artwork.height) / 2;
      const baselineRadius = radius + geometry.gap + geometry.fontSize * 0.78;
      const angleStep = geometry.characterAdvance / Math.max(1, baselineRadius);
      const halfSpan = Math.max(angleStep * 0.55, angleStep * characters.length / 2);
      const startAngle = Math.PI / 2 - halfSpan;
      const endAngle = Math.PI / 2 + halfSpan;
      const tubeDiameter = geometry.tubeDiameter;
      const backboardPadding = geometry.pixelsPerCm * contourCutClearanceCm;

      this.drawRoundContourBackboard(context, state, {
        ...geometry,
        angleStep,
        backboardPadding,
        baselineRadius,
        endAngle,
        halfSpan,
        radius,
        startAngle,
        tubeDiameter
      });

      characters.forEach((character, index) => {
        const offset = (index - (characters.length - 1) / 2) * angleStep;
        const x = geometry.artwork.centerX + baselineRadius * Math.sin(offset);
        const y = geometry.artwork.centerY + baselineRadius * Math.cos(offset);
        this.drawGlowCharacter(context, character, x, y, -offset, geometry.fontSize, state.color.value, 'alphabetic', tubeDiameter);
      });
    }

    getTextContourPoints(state, geometry) {
      const characters = Array.from(state.text);
      const points = [];
      characters.forEach((character, index) => {
        if (character === ' ') return;
        const glyph = this.getGlyphGeometry(character);
        if (!glyph) return;
        let x;
        let y;
        let rotation = 0;
        let baselineOffset = 0;
        if (state.layout === 'round') {
          const offset = (index - (characters.length - 1) / 2) * geometry.angleStep;
          x = geometry.artwork.centerX + geometry.baselineRadius * Math.sin(offset);
          y = geometry.artwork.centerY + geometry.baselineRadius * Math.cos(offset);
          rotation = -offset;
        } else if (state.layout === 'vertical') {
          x = geometry.textX;
          y = geometry.startY + geometry.characterAdvance * (index + 0.5);
          baselineOffset = geometry.fontSize * 0.42;
        } else {
          x = geometry.startX + geometry.characterAdvance * (index + 0.5);
          y = geometry.baseline;
        }
        const fontScale = geometry.fontSize * 0.88 / glyph.capAscent;
        const cosine = Math.cos(rotation);
        const sine = Math.sin(rotation);
        glyph.paths.forEach((path) => path.forEach((point) => {
          const px = (point[0] - glyph.centerX) * glyph.sourceScale * fontScale;
          const py = (glyph.top + (point[1] - glyph.minY) * glyph.sourceScale)
            * fontScale + baselineOffset;
          points.push([x + px * cosine - py * sine, y + px * sine + py * cosine]);
        }));
      });
      return points;
    }

    createBackboardMask(geometry) {
      const maskScale = 3;
      const mask = document.createElement('canvas');
      mask.width = Math.max(1, Math.ceil(geometry.width * maskScale));
      mask.height = Math.max(1, Math.ceil(geometry.height * maskScale));
      const maskContext = mask.getContext('2d');
      if (!maskContext) return null;
      maskContext.scale(maskScale, maskScale);
      maskContext.lineCap = 'round';
      maskContext.lineJoin = 'round';
      maskContext.fillStyle = '#fff';
      maskContext.strokeStyle = '#fff';
      maskContext.lineWidth = (geometry.backboardPadding + geometry.tubeDiameter * 0.5) * 2;
      return { mask, maskContext };
    }

    drawHorizontalContourBackboard(context, state, geometry) {
      const points = this.getTextContourPoints(state, geometry);
      if (!points.length) return;
      const layer = this.createBackboardMask(geometry);
      if (!layer) return;
      const { mask, maskContext } = layer;
      const cutRadius = geometry.backboardPadding + geometry.tubeDiameter * 0.5;
      const left = Math.min(...points.map((point) => point[0]));
      const right = Math.max(...points.map((point) => point[0]));
      const top = Math.min(geometry.artworkBottom - cutRadius * 0.3,
        ...points.map((point) => point[1]));
      const bottom = Math.max(...points.map((point) => point[1]));
      const bow = geometry.pixelsPerCm * 0.15;
      maskContext.beginPath();
      maskContext.moveTo(left, top);
      maskContext.lineTo(right, top);
      maskContext.lineTo(right, bottom);
      maskContext.quadraticCurveTo((left + right) / 2, bottom + bow * 2, left, bottom);
      maskContext.closePath();
      maskContext.fill();
      maskContext.stroke();
      this.paintAcrylicBackboard(context, mask, maskContext, geometry, (edgeContext) => {
        edgeContext.fillRect(left - cutRadius * 2, 0, right - left + cutRadius * 4,
          geometry.artworkBottom + cutRadius * 0.32);
      });
    }

    drawVerticalContourBackboard(context, state, geometry) {
      const points = this.getTextContourPoints(state, geometry);
      if (!points.length) return;
      const layer = this.createBackboardMask(geometry);
      if (!layer) return;
      const { mask, maskContext } = layer;
      const cutRadius = geometry.backboardPadding + geometry.tubeDiameter * 0.5;
      const connectorTop = Math.min(...points.map((point) => point[1]));
      const connectorBottom = Math.max(...points.map((point) => point[1]));
      const right = Math.max(...points.map((point) => point[0]));
      const bow = geometry.pixelsPerCm * 0.15;
      const profile = this.artworkJoinProfile;
      const artworkTop = geometry.artwork.centerY - geometry.artwork.height / 2;
      const artworkLeft = geometry.artwork.centerX - geometry.artwork.width / 2;
      const boundaryX = (y) => {
        const fraction = Math.max(0, Math.min(1, (y - artworkTop) / geometry.artwork.height));
        const end = Math.max(1, profile.findIndex((point) => point[0] >= fraction));
        const a = profile[end - 1];
        const b = profile[end];
        const t = (fraction - a[0]) / (b[0] - a[0]);
        return artworkLeft + geometry.artwork.width * (a[1] + (b[1] - a[1]) * t);
      };
      const joinYs = [connectorTop,
        ...profile.map((point) => artworkTop + point[0] * geometry.artwork.height)
          .filter((y) => y > connectorTop && y < connectorBottom),
        connectorBottom];
      const joinPoints = joinYs.map((y) => [boundaryX(y) - cutRadius * 0.65, y]);
      maskContext.beginPath();
      maskContext.moveTo(...joinPoints[0]);
      maskContext.lineTo(right, connectorTop);
      maskContext.quadraticCurveTo(right + bow * 2, (connectorTop + connectorBottom) / 2,
        right, connectorBottom);
      joinPoints.slice().reverse().forEach((point) => maskContext.lineTo(...point));
      maskContext.closePath();
      maskContext.fill();
      maskContext.stroke();
      this.paintAcrylicBackboard(context, mask, maskContext, geometry, (edgeContext) => {
        edgeContext.beginPath();
        edgeContext.moveTo(0, connectorTop - cutRadius * 2);
        edgeContext.lineTo(boundaryX(connectorTop) + cutRadius * 0.1, connectorTop - cutRadius * 2);
        joinYs.forEach((y) => edgeContext.lineTo(boundaryX(y) + cutRadius * 0.1, y));
        edgeContext.lineTo(boundaryX(connectorBottom) + cutRadius * 0.1, connectorBottom + cutRadius * 2);
        edgeContext.lineTo(0, connectorBottom + cutRadius * 2);
        edgeContext.closePath();
        edgeContext.fill();
      });
    }

    drawRoundContourBackboard(context, state, geometry) {
      const points = this.getTextContourPoints(state, geometry);
      if (!points.length) return;
      const layer = this.createBackboardMask(geometry);
      if (!layer) return;
      const { mask, maskContext } = layer;
      const cutRadius = geometry.backboardPadding + geometry.tubeDiameter * 0.5;
      const centerX = geometry.artwork.centerX;
      const centerY = geometry.artwork.centerY;
      const polarPoints = points.map(([x, y]) => ({
        radius: Math.hypot(x - centerX, y - centerY),
        angle: Math.atan2(y - centerY, x - centerX)
      }));
      const outerRadius = Math.max(...polarPoints.map((point) => point.radius));
      const startAngle = Math.min(...polarPoints.map((point) => point.angle));
      const endAngle = Math.max(...polarPoints.map((point) => point.angle));
      const innerRadius = Math.max(1, geometry.radius * 0.74 - cutRadius * 0.28);
      maskContext.beginPath();
      maskContext.arc(centerX, centerY, innerRadius, startAngle, endAngle);
      maskContext.arc(centerX, centerY, outerRadius, endAngle, startAngle, true);
      maskContext.closePath();
      maskContext.fill();
      maskContext.stroke();
      const anglePadding = Math.asin(Math.min(0.9, cutRadius / Math.max(1, innerRadius)));
      this.paintAcrylicBackboard(context, mask, maskContext, geometry, (edgeContext) => {
        edgeContext.beginPath();
        edgeContext.moveTo(centerX, centerY);
        edgeContext.arc(centerX, centerY, geometry.radius + cutRadius * 0.32,
          startAngle - anglePadding, endAngle + anglePadding);
        edgeContext.closePath();
        edgeContext.fill();
      });
    }

    paintAcrylicBackboard(context, mask, maskContext, geometry, clearJoinedEdge) {
      const maskScale = mask.width / Math.max(1, geometry.width);
      maskContext.setTransform(1, 0, 0, 1, 0, 0);
      // Acrylic is a filled, continuous sheet; the counters in letters such
      // as O, B and D must not become holes cut out of that sheet.
      this.fillBackboardHoles(maskContext, mask.width, mask.height);

      const glass = document.createElement('canvas');
      glass.width = mask.width;
      glass.height = mask.height;
      const glassContext = glass.getContext('2d');
      if (!glassContext) return;
      glassContext.drawImage(mask, 0, 0);
      glassContext.globalCompositeOperation = 'source-in';
      const glassGradient = glassContext.createLinearGradient(0, 0, 0, glass.height);
      glassGradient.addColorStop(0, 'rgba(250, 255, 255, 0.96)');
      glassGradient.addColorStop(0.48, 'rgba(196, 225, 236, 0.78)');
      glassGradient.addColorStop(1, 'rgba(244, 252, 255, 0.90)');
      glassContext.fillStyle = glassGradient;
      glassContext.fillRect(0, 0, glass.width, glass.height);

      context.save();
      context.imageSmoothingEnabled = true;
      context.globalAlpha = 0.105;
      context.shadowColor = 'rgba(214, 240, 250, 0.42)';
      context.shadowBlur = 1.8;
      context.drawImage(glass, 0, 0, geometry.width, geometry.height);
      context.shadowBlur = 0;
      context.globalAlpha = 0.018;
      context.drawImage(glass, 0.35, 0.55, geometry.width, geometry.height);
      context.globalAlpha = 1;

      const edgeCanvas = document.createElement('canvas');
      edgeCanvas.width = mask.width;
      edgeCanvas.height = mask.height;
      const edgeContext = edgeCanvas.getContext('2d');
      if (!edgeContext) {
        context.restore();
        return;
      }
      const outline = Math.max(1, Math.round(maskScale * 0.35));
      [
        [-outline, -outline], [0, -outline], [outline, -outline],
        [-outline, 0],                         [outline, 0],
        [-outline, outline],  [0, outline],  [outline, outline]
      ].forEach(([offsetX, offsetY]) => {
        edgeContext.drawImage(mask, offsetX, offsetY);
      });
      edgeContext.globalCompositeOperation = 'destination-out';
      edgeContext.drawImage(mask, 0, 0);
      edgeContext.globalCompositeOperation = 'source-in';
      edgeContext.fillStyle = 'rgb(226, 243, 249)';
      edgeContext.fillRect(0, 0, edgeCanvas.width, edgeCanvas.height);
      if (clearJoinedEdge) {
        edgeContext.save();
        edgeContext.globalCompositeOperation = 'destination-out';
        edgeContext.setTransform(maskScale, 0, 0, maskScale, 0, 0);
        edgeContext.fillStyle = '#000';
        clearJoinedEdge(edgeContext);
        edgeContext.restore();
      }
      context.globalAlpha = 0.48;
      context.filter = 'blur(0.16px)';
      context.drawImage(edgeCanvas, 0, 0, geometry.width, geometry.height);
      context.filter = 'none';
      context.globalAlpha = 0.22;
      context.drawImage(edgeCanvas, 0.45, 0.55, geometry.width, geometry.height);
      context.globalAlpha = 1;
      context.restore();
    }

    getGlyphGeometry(character) {
      const cached = this.glyphGeometryCache.get(character);
      if (cached) return cached;
      const glyph = this.singleLineGlyphData[character];
      if (!glyph?.paths?.length || !glyph.advance) return null;
      const points = glyph.paths.flat();
      const minX = Math.min(...points.map((point) => point[0]));
      const maxX = Math.max(...points.map((point) => point[0]));
      const minY = Math.min(...points.map((point) => point[1]));
      const maxY = Math.max(...points.map((point) => point[1]));
      const metrics = this.getNativeTextMetrics(character);
      // The stored Arial traces were normalized separately for every glyph.
      // Recover their common font scale from the original advance, then use
      // ONE capital-height reference for the complete alphabet. In particular,
      // a/e stay at x-height, descenders remain below the shared baseline, and
      // a dash is no longer enlarged to the height of a capital letter.
      const sourceScale = metrics.width / glyph.advance;
      const capAscent = this.getNativeTextMetrics('H').ascent;
      const inset = (metrics.height - (maxY - minY) * sourceScale) / 2;
      const result = {
        paths: glyph.paths,
        centerX: (minX + maxX) / 2,
        minY,
        maxY,
        sourceScale,
        top: -metrics.ascent + inset,
        capAscent
      };
      this.glyphGeometryCache.set(character, result);
      return result;
    }

    traceGlowCharacterPath(context, character, x, y, rotation, fontSize, baseline) {
      if (character === ' ') return false;
      const glyph = this.getGlyphGeometry(character);
      if (!glyph) return false;
      const fontScale = fontSize * 0.88 / glyph.capAscent;
      const baselineOffset = baseline === 'middle' ? fontSize * 0.42 : 0;
      context.save();
      context.translate(x, y);
      context.rotate(rotation);
      glyph.paths.forEach((path) => {
        path.forEach((point, index) => {
          const px = (point[0] - glyph.centerX) * glyph.sourceScale * fontScale;
          const py = (glyph.top + (point[1] - glyph.minY) * glyph.sourceScale)
            * fontScale + baselineOffset;
          if (index === 0) context.moveTo(px, py);
          else context.lineTo(px, py);
        });
      });
      context.restore();
      return true;
    }

    drawGlowCharacter(context, character, x, y, rotation, fontSize, color, baseline, tubeDiameter) {
      if (character === ' ') return;
      context.save();
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      if (!this.traceGlowCharacterPath(context, character, x, y, rotation, fontSize, baseline)) {
        context.restore();
        return;
      }
      // A colored 5 mm tube, a continuous luminous core and a soft falloff.
      // No filled type or outline font is substituted for the LED centerline.
      context.globalCompositeOperation = 'lighter';
      context.globalAlpha = 0.14;
      context.strokeStyle = color;
      context.lineWidth = tubeDiameter * 3.4;
      context.shadowColor = color;
      context.shadowBlur = tubeDiameter * 5.5;
      context.stroke();

      context.globalAlpha = 0.36;
      context.lineWidth = tubeDiameter * 1.8;
      context.shadowBlur = tubeDiameter * 2.8;
      context.stroke();

      context.globalCompositeOperation = 'source-over';
      context.globalAlpha = 1;
      context.strokeStyle = this.mixWithWhite(color, 0.16);
      context.lineWidth = tubeDiameter;
      context.shadowBlur = tubeDiameter * 1.4;
      context.stroke();

      context.strokeStyle = this.mixWithWhite(color, 0.8);
      context.lineWidth = tubeDiameter * 0.42;
      context.shadowColor = '#fff';
      context.shadowBlur = tubeDiameter * 0.45;
      context.stroke();
      context.restore();
    }

    getNativeTextMetrics(text) {
      const value = String(text || '');
      const cached = this.nativeTextMeasureCache.get(value);
      if (cached) return cached;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return { width: 0, height: 100, ascent: 74, descent: 26 };

      context.font = '100px Arial';
      context.textBaseline = 'alphabetic';
      const measured = context.measureText(value);
      const ascent = Number(measured.actualBoundingBoxAscent ?? measured.fontBoundingBoxAscent ?? 74);
      const descent = Number(measured.actualBoundingBoxDescent ?? measured.fontBoundingBoxDescent ?? 0);
      const metrics = {
        width: Math.max(0, Number(measured.width) || 0),
        height: Math.max(1, ascent + descent),
        ascent,
        descent
      };

      if (this.nativeTextMeasureCache.size >= 64) {
        const firstKey = this.nativeTextMeasureCache.keys().next().value;
        this.nativeTextMeasureCache.delete(firstKey);
      }
      this.nativeTextMeasureCache.set(value, metrics);
      return metrics;
    }

    fillBackboardHoles(context, width, height) {
      const image = context.getImageData(0, 0, width, height);
      const outside = new Uint8Array(width * height);
      // A typed queue keeps the flood-fill linear and avoids allocating one
      // JavaScript number object per canvas pixel while the customer types.
      const queue = new Int32Array(width * height);
      let cursor = 0;
      let queueLength = 0;
      const enqueue = (x, y) => {
        const pixel = y * width + x;
        if (outside[pixel] || image.data[pixel * 4 + 3] >= 64) return;
        outside[pixel] = 1;
        queue[queueLength] = pixel;
        queueLength += 1;
      };

      for (let x = 0; x < width; x += 1) {
        enqueue(x, 0);
        enqueue(x, height - 1);
      }
      for (let y = 1; y < height - 1; y += 1) {
        enqueue(0, y);
        enqueue(width - 1, y);
      }

      while (cursor < queueLength) {
        const pixel = queue[cursor];
        cursor += 1;
        const x = pixel % width;
        const y = Math.floor(pixel / width);
        if (x > 0) enqueue(x - 1, y);
        if (x + 1 < width) enqueue(x + 1, y);
        if (y > 0) enqueue(x, y - 1);
        if (y + 1 < height) enqueue(x, y + 1);
      }

      for (let pixel = 0; pixel < outside.length; pixel += 1) {
        const alpha = pixel * 4 + 3;
        if (outside[pixel] || image.data[alpha] >= 64) continue;
        image.data[alpha - 3] = 255;
        image.data[alpha - 2] = 255;
        image.data[alpha - 1] = 255;
        image.data[alpha] = 255;
      }
      context.putImageData(image, 0, 0);
    }

    mixWithWhite(hex, amount) {
      const normalized = String(hex || '#ffffff').replace('#', '');
      const value = normalized.length === 3
        ? normalized.split('').map((character) => character + character).join('')
        : normalized.padEnd(6, 'f').slice(0, 6);
      const channels = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
      const mixed = channels.map((channel) => Math.round(channel + (255 - channel) * amount));
      return `rgb(${mixed.join(', ')})`;
    }

    getTextLayout(text, rules, characterCount = Array.from(text || '').length) {
      const count = Math.max(0, Number(characterCount) || 0);
      const naturalWidthCm = rules.characterWidthCm * count;
      const uniformScale = naturalWidthCm > 0
        ? Math.min(1, rules.maxTextWidthCm / naturalWidthCm)
        : 1;
      return {
        maxSlotWidthCm: rules.maxTextWidthCm,
        naturalWidthCm,
        renderedWidthCm: naturalWidthCm * uniformScale,
        renderedCellCm: rules.characterWidthCm * uniformScale,
        uniformScale,
        textScaleX: 1
      };
    }

  }

  Object.assign(LEDTextPreviewRenderer, {
    sizeRules,
    productColors,
    previewFontFamily,
    contourCutClearanceCm,
    tubeDiameterCm,
    artworkTextGapCm,
    textOccupancyRatio
  });
  window.LEDTextPreviewRenderer = LEDTextPreviewRenderer;
})();
